import Colors from "modules/Colors";
import { PlanType } from "modules/Const";
import { clsx, styx } from "modules/Utils";
import { CSSProperties } from "react";
import Icon from "./Icon";

export enum BrandTierType {
    TAG = 'tag',
    TEXT = 'text',
}

interface BrandTierProps {
    className: string;
    style?: CSSProperties;
    tier: PlanType;
    type?: BrandTierType;
    iconSize?: number;
    textSize?: number;
    isWhiteBackground?: boolean;
}

const BrandTier = ({
    className,
    style,
    tier,
    type = BrandTierType.TAG,
    iconSize = 12,
    textSize = 10,
    isWhiteBackground = false,
}: BrandTierProps) => {
    /** Get the text to display based on the tier */
    const getBrandTierText = () => {
        switch (tier) {
            case PlanType.TEAM:
                return 'TEAM';
            case PlanType.PRO:
                return 'PRO';
            case PlanType.LITE:
                return 'LITE';
            case PlanType.STARTER:
                return 'STARTER';
            case PlanType.GROWTH:
                return 'GROWTH';
            case PlanType.TEAM_V2:
                return 'TEAM PLAN';
            default:
                return '';
        }
    }

    /** Get the background color based on the tier */
    const getBackgroundColor = () => {
        switch (tier) {
            case PlanType.TEAM:
            case PlanType.PRO:
            case PlanType.GROWTH:
            case PlanType.TEAM_V2:
                return Colors.gold;
            case PlanType.LITE:
            case PlanType.STARTER:
                return Colors.purple;
            default:
                return '';
        }
    }

    const getTextColor = () => {
        switch (tier) {
            case PlanType.TEAM:
            case PlanType.PRO:
            case PlanType.GROWTH:
            case PlanType.TEAM_V2:
                if (isWhiteBackground) {
                    return Colors.goldDark;
                } else {
                    return Colors.gold;
                }
            case PlanType.LITE:
            case PlanType.STARTER:
                return Colors.purple;
            default:
                return '';
        }
    }

    /** Get the icon color based on the tier */
    const getIconColor = () => {
        switch (tier) {
            case PlanType.TEAM:
            case PlanType.PRO:
            case PlanType.GROWTH:
            case PlanType.TEAM_V2:
                if (isWhiteBackground) {
                    return Colors.goldDark;
                } else {
                    return Colors.yellow;
                }
            case PlanType.LITE:
            case PlanType.STARTER:
                return Colors.white;
            default:
                return '';
        }
    }

    const tierText = getBrandTierText();

    if (!tierText) {
        return null;
    }

    if (type === BrandTierType.TEXT) {
        return (
            <div
                className={clsx("brandTierTextElement_text", className)}
                style={styx(
                    sty.brandTierText_text,
                    {
                        fontSize: textSize,
                    },
                    style
                )}
            >
                <Icon
                    className="brandTierTextIcon"
                    icon="gem"
                    color={getTextColor()}
                    size={iconSize}
                />
                <div style={{ color: getTextColor() }}>
                    {tierText}
                </div>
            </div>
        )
    }

    return (
        <div
            className={clsx('brandTierTagElement_tag', className)}
            style={styx(
                sty.brandTierTag,
                {
                    backgroundColor: getBackgroundColor(),
                },
                style
            )}
        >
            <Icon
                className="brandTierTagIcon"
                icon="stars"
                color={getIconColor()}
                size={18}
            />
            <div
                className="brandTierTagText"
                style={sty.brandTierTagText}
            >
                {tierText}
            </div>
        </div>
    )
};

const sty: Record<string, CSSProperties> = {
    brandTierTag: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        color: Colors.white,
        paddingLeft: 5,
        paddingRight: 5,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 4,
        fontWeight: 500,
        width: 'fit-content',
        flexShrink: 0,
    },
    brandTierTagText: {
        marginTop: 1,
        marginBottom: -2,
        fontSize: 14,
    },
    brandTierText_text: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        fontSize: 10,
        fontWeight: 400,
    }
}

export default BrandTier;